import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import {
  ArrowLeft, Download, FileText, File, Image, Video,
  MessageSquare, Send, CheckCircle, Lock, Eye, EyeOff, Loader2, X,
  ChevronRight, ChevronDown, FileSpreadsheet, Presentation,
  Reply, Bold, Italic, Link as LinkIcon, List, Plus, Trash2, MapPin,
  ZoomIn, ZoomOut, ChevronLeft, StickyNote
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';

// Set PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getFileIcon = (mimeType, filename) => {
  if (mimeType?.includes('image')) return Image;
  if (mimeType?.includes('video')) return Video;
  if (mimeType?.includes('pdf')) return FileText;
  if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel') || filename?.match(/\.xlsx?$/i)) return FileSpreadsheet;
  if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint') || filename?.match(/\.pptx?$/i)) return Presentation;
  if (mimeType?.includes('word') || mimeType?.includes('document') || filename?.match(/\.docx?$/i)) return FileText;
  return File;
};

// =============================================================================
// MAIN DOCUMENT VIEWER PAGE
// =============================================================================

const DocumentViewer = ({ currentOrganization }) => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const organizationId = currentOrganization?.id || null;
  const committeeId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('committee_id');
  }, [location.search]);
  const withCommittee = useCallback((url) => {
    if (!committeeId) return url;
    return `${url}${url.includes('?') ? '&' : '?'}committee_id=${committeeId}`;
  }, [committeeId]);

  const currentUser = useMemo(() => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return null;
      const payload = token.split('.')[1];
      if (!payload) return null;
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
      const decoded = JSON.parse(atob(padded));
      return decoded?.sub ? {
        id: String(decoded.sub),
        name: decoded.name || decoded.full_name || decoded.email || 'You',
        email: decoded.email || null,
        avatar_url: decoded.avatar_url || null
      } : null;
    } catch {
      return null;
    }
  }, []);
  const currentUserId = currentUser?.id || null;

  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('comments');

  // Marks/Annotations state
  const [marks, setMarks] = useState([]);
  const [selectedMark, setSelectedMark] = useState(null);
  const [isAddingMark, setIsAddingMark] = useState(false);
  const [showAnnotations, setShowAnnotations] = useState(true); // Toggle for hiding/showing annotations

  // Private notes state (visible only to the current user)
  const [privateNotes, setPrivateNotes] = useState([]);
  const [selectedPrivateNote, setSelectedPrivateNote] = useState(null);
  const [isAddingPrivateNote, setIsAddingPrivateNote] = useState(false);
  const [draftPrivateNote, setDraftPrivateNote] = useState(null);

  // Text selection state (for Google Docs-style commenting)
  const [textSelection, setTextSelection] = useState(null);

  // Comments state (general comments without mark_id)
  const [comments, setComments] = useState([]);
  const [members, setMembers] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const textareaRef = useRef(null);
  const previewContainerRef = useRef(null);

  // Fetch document details
  const fetchDocument = useCallback(async () => {
    if (!documentId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(withCommittee(`/board/documents/${documentId}`));
      setDocument(response.data);
    } catch (err) {
      console.error('Failed to fetch document:', err);
      setError(err.response?.data?.detail || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  }, [documentId, organizationId, withCommittee]);

  const normalizeComments = (apiComments) => {
    const flatten = (items = [], parentId = null) => {
      const out = [];
      for (const item of items) {
        const { replies = [], ...base } = item || {};
        out.push({
          ...base,
          parent_comment_id: parentId
        });
        if (Array.isArray(replies) && replies.length > 0) {
          out.push(...flatten(replies, base.id));
        }
      }
      return out;
    };

    if (!Array.isArray(apiComments)) return [];
    return flatten(apiComments);
  };

  // Fetch marks and comments
  const fetchMarksAndComments = useCallback(async () => {
    if (!documentId) return;

    try {
      const [marksRes, commentsRes, membersRes, privateNotesRes] = await Promise.all([
        api.get(withCommittee(`/board/documents/${documentId}/marks`)),
        api.get(withCommittee(`/board/documents/${documentId}/comments`)),
        api.get(withCommittee('/board/documents/members')),
        api.get(withCommittee(`/board/documents/${documentId}/private-notes`))
      ]);

      setMarks(marksRes.data.marks || []);
      setComments(normalizeComments(commentsRes.data.comments || []));
      setMembers(membersRes.data.members || []);
      setPrivateNotes(privateNotesRes.data.private_notes || []);
    } catch (err) {
      console.error('Failed to fetch marks/comments:', err);
    }
  }, [documentId, organizationId, withCommittee]);

  useEffect(() => {
    fetchDocument();
    fetchMarksAndComments();
  }, [fetchDocument, fetchMarksAndComments]);

  useEffect(() => {
    if (!organizationId) return;
    setDocument(null);
    setError(null);
    setLoading(true);
  }, [organizationId]);

  // Create a new mark (optionally with highlighted text and rectangles)
  const createMark = async (x_percent, y_percent, page_number = 1, highlightedText = null, highlightedTextRects = null) => {
    try {
      const response = await api.post(withCommittee(`/board/documents/${documentId}/marks`), {
        x_percent,
        y_percent,
        page_number,
        highlighted_text: highlightedText,
        highlighted_text_rects: highlightedTextRects
      });
      setMarks(prev => [...prev, response.data]);
      setSelectedMark(response.data);
      setIsAddingMark(false);
      setTextSelection(null);
      setActiveTab('annotations');
      toast.success(`Mark ${response.data.label} created`);
    } catch (err) {
      toast.error('Failed to create mark');
    }
  };

  const createPrivateNote = async ({ content, x_percent = null, y_percent = null, page_number = 1, highlighted_text = null, highlighted_text_rects = null }) => {
    const trimmed = content?.trim();
    if (!trimmed) return;
    try {
      const response = await api.post(withCommittee(`/board/documents/${documentId}/private-notes`), {
        content: trimmed,
        x_percent,
        y_percent,
        page_number,
        highlighted_text,
        highlighted_text_rects
      });
      setPrivateNotes(notes => [...notes, response.data]);
      setSelectedPrivateNote(response.data);
      setIsAddingPrivateNote(false);
      setTextSelection(null);
      setActiveTab('private');
      toast.success('Private note added');
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add private note');
      return false;
    }
  };

  const beginPrivateNoteDraft = (draft) => {
    setDraftPrivateNote({
      content: '',
      x_percent: draft.x_percent,
      y_percent: draft.y_percent,
      page_number: draft.page_number || 1,
      highlighted_text: draft.highlighted_text || null,
      highlighted_text_rects: draft.highlighted_text_rects || null
    });
    setSelectedPrivateNote(null);
    setIsAddingPrivateNote(false);
    setIsAddingMark(false);
    setActiveTab('private');
  };

  const saveDraftPrivateNote = async () => {
    if (!draftPrivateNote?.content?.trim()) return;
    const saved = await createPrivateNote(draftPrivateNote);
    if (saved) setDraftPrivateNote(null);
  };

  const cancelDraftPrivateNote = () => {
    setDraftPrivateNote(null);
    setIsAddingPrivateNote(false);
  };

  const createPrivateNoteFromSelection = () => {
    if (!textSelection) return;
    beginPrivateNoteDraft({
      x_percent: textSelection.x,
      y_percent: textSelection.y,
      page_number: textSelection.pageNumber,
      highlighted_text: textSelection.text,
      highlighted_text_rects: textSelection.rects
    });
    setTextSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  const updatePrivateNote = async (noteId, content) => {
    const trimmed = content?.trim();
    if (!trimmed) return;
    try {
      const response = await api.put(withCommittee(`/board/documents/${documentId}/private-notes/${noteId}`), { content: trimmed });
      setPrivateNotes(notes => notes.map(n => n.id === noteId ? response.data : n));
      if (selectedPrivateNote?.id === noteId) setSelectedPrivateNote(response.data);
      toast.success('Private note updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update private note');
    }
  };

  const deletePrivateNote = async (noteId) => {
    if (!window.confirm('Delete this private note?')) return;
    try {
      await api.delete(withCommittee(`/board/documents/${documentId}/private-notes/${noteId}`));
      setPrivateNotes(notes => notes.filter(n => n.id !== noteId));
      if (selectedPrivateNote?.id === noteId) setSelectedPrivateNote(null);
      toast.success('Private note deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete private note');
    }
  };

  // Handle text selection from PDF viewer
  const handleTextSelect = (selection) => {
    setTextSelection(selection);
  };

  // Create mark from text selection
  const createMarkFromSelection = () => {
    if (textSelection) {
      createMark(
        textSelection.x,
        textSelection.y,
        textSelection.pageNumber,
        textSelection.text,
        textSelection.rects  // Pass the rectangle coordinates
      );
    }
  };

  // Cancel text selection popup
  const cancelTextSelection = () => {
    setTextSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  // Delete a mark
  const deleteMark = async (markId) => {
    if (!window.confirm('Delete this mark and all its comments?')) return;

    try {
      await api.delete(withCommittee(`/board/documents/${documentId}/marks/${markId}`));
      setMarks(marks.filter(m => m.id !== markId));
      if (selectedMark?.id === markId) setSelectedMark(null);
      fetchMarksAndComments();
      toast.success('Mark deleted');
    } catch (err) {
      toast.error('Failed to delete mark');
    }
  };

  // Submit general comment (no mark_id) or comment on mark
  const submitComment = async (parentId = null, markId = null) => {
    const content = newComment.trim();
    if (!content) return;

    setSubmitting(true);
    try {
      const response = await api.post(withCommittee(`/board/documents/${documentId}/comments`), {
        content,
        mark_id: markId || null,
        mentioned_user_ids: extractMentions(content),
        parent_comment_id: parentId
      });
      const createdComment = {
        id: response.data?.id || `local-${Date.now()}`,
        document_id: documentId,
        mark_id: markId || null,
        parent_comment_id: parentId || null,
        content,
        is_resolved: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        author: currentUser || { id: currentUserId, name: 'You' }
      };
      setComments(prev => [...prev.filter(c => c.id !== createdComment.id), createdComment]);
      if (markId) {
        setMarks(prev => prev.map(mark => mark.id === markId
          ? { ...mark, comment_count: (mark.comment_count || 0) + 1 }
          : mark
        ));
      }
      setNewComment('');
      setReplyingTo(null);
      setShowPreview(false);
      toast.success(parentId ? 'Reply added' : 'Comment added');
      fetchMarksAndComments().catch((refreshErr) => {
        console.warn('Comment saved, but refresh failed:', refreshErr);
      });
    } catch (err) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  // Insert markdown formatting
  const insertFormatting = (prefix, suffix = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = newComment;
    const selectedText = text.substring(start, end);

    const newText = text.substring(0, start) + prefix + selectedText + suffix + text.substring(end);
    setNewComment(newText);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = selectedText ? start + prefix.length + selectedText.length + suffix.length : start + prefix.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Insert link
  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      const textarea = textareaRef.current;
      const start = textarea?.selectionStart || 0;
      const end = textarea?.selectionEnd || 0;
      const text = newComment;
      const selectedText = text.substring(start, end) || 'link text';

      const newText = text.substring(0, start) + `[${selectedText}](${url})` + text.substring(end);
      setNewComment(newText);
    }
  };

  // Handle @ mention input
  const handleCommentChange = (e) => {
    const value = e.target.value;
    setNewComment(value);

    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = value.substring(lastAtIndex + 1);
      if (!textAfterAt.includes(' ') && textAfterAt.length > 0) {
        setMentionFilter(textAfterAt.toLowerCase());
        setShowMentions(true);
      } else if (textAfterAt.length === 0) {
        setMentionFilter('');
        setShowMentions(true);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  // Insert mention
  const insertMention = (member) => {
    const lastAtIndex = newComment.lastIndexOf('@');
    const newText = newComment.substring(0, lastAtIndex) + `@${member.name} `;
    setNewComment(newText);
    setShowMentions(false);
    textareaRef.current?.focus();
  };

  // Filter members for mentions
  const filteredMembers = members.filter(m =>
    (m.has_board_access || m.role === 'admin' || m.role === 'owner') &&
    m.name.toLowerCase().includes(mentionFilter)
  );

  // Extract @mentions from text
  const extractMentions = (text) => {
    const mentionRegex = /@(\w+)/g;
    const mentions = [];
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
      const member = members.find(m =>
        m.name.toLowerCase().includes(match[1].toLowerCase())
      );
      if (member) mentions.push(member.id);
    }
    return mentions;
  };

  // Resolve comment
  const resolveComment = async (commentId) => {
    try {
      await api.patch(withCommittee(`/board/documents/${documentId}/comments/${commentId}/resolve`));
      fetchMarksAndComments();
      toast.success('Comment resolved');
    } catch (err) {
      toast.error('Failed to resolve comment');
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      await api.delete(withCommittee(`/board/documents/${documentId}/comments/${commentId}`));
      fetchMarksAndComments();
      toast.success('Comment deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete comment');
    }
  };

  // Go back to documents
  const goBack = () => {
    navigate('/governance');
  };

  // Get general comments (not linked to a mark)
  const generalComments = comments.filter(c => !c.mark_id && !c.parent_comment_id);

  // Get comments for a specific mark
  const getCommentsForMark = (markId) => {
    return comments.filter(c => c.mark_id === markId && !c.parent_comment_id);
  };

  // Get replies for a comment
  const getReplies = (commentId) => {
    return comments.filter(c => c.parent_comment_id === commentId);
  };

  // Handle click on document preview for adding marks
  const handlePreviewClick = (e) => {
    if ((!isAddingMark && !isAddingPrivateNote) || !previewContainerRef.current) return;

    const rect = previewContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    if (isAddingPrivateNote) {
      beginPrivateNoteDraft({ x_percent: x, y_percent: y, page_number: 1 });
      return;
    }
    createMark(x, y);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading document...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Document Not Found</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <Button onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Documents
          </Button>
        </div>
      </div>
    );
  }

  if (!document) return null;

  const IconComponent = getFileIcon(document.mime_type, document.original_filename);

  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="h-6 w-px bg-gray-200" />

          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <IconComponent className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">{document.name}</h1>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="capitalize">{document.document_type}</span>
                <span>•</span>
                <span>{formatFileSize(document.file_size)}</span>
                {document.is_locked && (
                  <>
                    <span>•</span>
                    <Lock className="h-3 w-3 text-amber-500" />
                    <span className="text-purple-600">Executive Only</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Toggle Annotations */}
          <Button
            variant={showAnnotations ? "default" : "outline"}
            size="sm"
            onClick={() => setShowAnnotations(!showAnnotations)}
            className={showAnnotations ? "bg-purple-600 hover:bg-purple-700" : ""}
          >
            {showAnnotations ? (
              <>
                <Eye className="h-4 w-4 mr-2" />
                Annotations On
              </>
            ) : (
              <>
                <EyeOff className="h-4 w-4 mr-2" />
                Annotations Off
              </>
            )}
          </Button>

          <div className="h-6 w-px bg-gray-200" />

          {document.pdf_preview_url && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(document.pdf_preview_url, '_blank')}
            >
              <Eye className="h-4 w-4 mr-2" />
              Open Preview
            </Button>
          )}
          {document.can_download && document.file_url && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(document.file_url, '_blank')}
            >
              <Download className="h-4 w-4 mr-2" />
              Download Original
            </Button>
          )}
        </div>
      </header>

      {/* Main Content - Side by Side */}
      <div className="flex-1 flex overflow-hidden">
        {/* Document Preview - Left Side */}
        <div className="flex-1 bg-gray-200 p-2 overflow-auto relative">
          <div
            ref={previewContainerRef}
            className="bg-white rounded-xl shadow-sm h-full min-h-[calc(100vh-140px)] overflow-hidden relative"
          >
            {/* Floating Add Buttons - Always visible in corner */}
            {showAnnotations && !isAddingMark && !isAddingPrivateNote && (
              <div className="absolute bottom-4 right-4 z-20 flex gap-2">
                <Button
                  onClick={() => { setIsAddingPrivateNote(true); setActiveTab('private'); }}
                  className="bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white shadow-lg rounded-full h-12 px-5"
                >
                  <StickyNote className="h-5 w-5 mr-2" />
                  Private Note
                </Button>
                <Button
                  onClick={() => { setIsAddingMark(true); setIsAddingPrivateNote(false); }}
                  className="bg-gradient-to-r from-fuchsia-600 to-pink-600 hover:from-fuchsia-700 hover:to-pink-700 text-white shadow-lg rounded-full h-12 px-5"
                >
                  <MapPin className="h-5 w-5 mr-2" />
                  Add Mark
                </Button>
              </div>
            )}

            {/* Document Preview Content */}
            <div className="relative h-full">
              <DocumentPreview
                document={document}
                onTextSelect={handleTextSelect}
                marks={marks}
                selectedMarkId={selectedMark?.id}
                onSelectMark={(mark) => { setSelectedMark(mark); setActiveTab('annotations'); }}
                isAddingMark={isAddingMark}
                onPlaceMark={(draft) => createMark(draft.x_percent, draft.y_percent, draft.page_number)}
                onCancelAddMark={() => setIsAddingMark(false)}
                showAnnotations={showAnnotations}
                committeeId={committeeId}
                privateNotes={privateNotes}
                selectedPrivateNote={selectedPrivateNote}
                onSelectPrivateNote={(note) => { setSelectedPrivateNote(note); setActiveTab('private'); }}
                draftPrivateNote={draftPrivateNote}
                setDraftPrivateNote={setDraftPrivateNote}
                saveDraftPrivateNote={saveDraftPrivateNote}
                cancelDraftPrivateNote={cancelDraftPrivateNote}
                isAddingPrivateNote={isAddingPrivateNote}
                onPlacePrivateNote={(draft) => beginPrivateNoteDraft(draft)}
              />
            </div>

            {/* Text Selection Popup - appears when user highlights text */}
            {textSelection && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={cancelTextSelection}
                />

                {/* Popup */}
                <div
                  className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden min-w-[250px]"
                  style={{
                    left: Math.min(textSelection.screenX - 125, window.innerWidth - 270),
                    top: textSelection.screenY + 10
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-3 border-b border-gray-100 bg-gray-50">
                    <p className="text-xs text-gray-500 mb-1">Selected text:</p>
                    <p className="text-sm text-gray-800 font-medium line-clamp-2">
                      "{textSelection.text.substring(0, 100)}{textSelection.text.length > 100 ? '...' : ''}"
                    </p>
                  </div>
                  <button
                    onClick={createMarkFromSelection}
                    className="flex items-center gap-3 px-4 py-3 w-full hover:bg-purple-50 transition-colors text-left"
                  >
                    <div className="p-1.5 bg-purple-100 rounded-full">
                      <MessageSquare className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Add Comment</p>
                      <p className="text-xs text-gray-500">Start a discussion about this text</p>
                    </div>
                  </button>
                  <button
                    onClick={createPrivateNoteFromSelection}
                    className="flex items-center gap-3 px-4 py-3 w-full hover:bg-purple-50 transition-colors text-left border-t border-gray-100"
                  >
                    <div className="p-1.5 bg-purple-100 rounded-full">
                      <StickyNote className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Add Private Note</p>
                      <p className="text-xs text-gray-500">Only you can see this note</p>
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Comments & Annotations Panel - Right Side */}
        <div className="w-[420px] xl:w-[460px] bg-white border-l border-gray-200 flex flex-col h-full min-h-0 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden">
            <TabsList className="flex-shrink-0 grid w-auto grid-cols-3 gap-1 p-1 m-2 bg-gray-100 rounded-lg">
              <TabsTrigger value="comments" className="min-w-0 flex items-center justify-center gap-1.5 px-2 text-xs xl:text-sm whitespace-nowrap">
                <MessageSquare className="h-4 w-4 flex-shrink-0" />
                <span>Comments</span>
                {generalComments.length > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-[10px] xl:text-xs flex-shrink-0">{generalComments.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="annotations" className="min-w-0 flex items-center justify-center gap-1.5 px-2 text-xs xl:text-sm whitespace-nowrap">
                <MapPin className="h-4 w-4 flex-shrink-0" />
                <span>Annotations</span>
                {marks.length > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-[10px] xl:text-xs flex-shrink-0">{marks.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="private" className="min-w-0 flex items-center justify-center gap-1.5 px-2 text-xs xl:text-sm whitespace-nowrap">
                <StickyNote className="h-4 w-4 flex-shrink-0" />
                <span>Private</span>
                {privateNotes.length > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-[10px] xl:text-xs flex-shrink-0">{privateNotes.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Comments Tab */}
            <TabsContent value="comments" className="flex-1 min-h-0 m-0 overflow-hidden data-[state=active]:grid data-[state=active]:grid-rows-[minmax(0,1fr)_auto]">
              {/* Scrollable comment list - forced to take all space above the composer */}
              <div className="min-h-0 overflow-y-auto p-4">
                {generalComments.length === 0 ? (
                  <div className="h-full min-h-[260px] flex flex-col items-center justify-center text-center text-gray-500">
                    <MessageSquare className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium mb-1">No comments yet</p>
                    <p className="text-sm">Start a conversation about this document.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {generalComments.map(comment => (
                      <CommentThread
                        key={comment.id}
                        comment={comment}
                        replies={getReplies(comment.id)}
                        onResolve={resolveComment}
                        onDelete={deleteComment}
                        currentUserId={currentUserId}
                        onReply={(c) => {
                          setReplyingTo(c);
                          textareaRef.current?.focus();
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Comment Input - Fixed at bottom */}
              <div className="border-t border-gray-200 bg-gray-50">
                <CommentInput
                  value={newComment}
                  onChange={handleCommentChange}
                  onSubmit={() => submitComment(replyingTo?.id, null)}
                  onFormatting={insertFormatting}
                  onInsertLink={insertLink}
                  submitting={submitting}
                  replyingTo={replyingTo}
                  onCancelReply={() => setReplyingTo(null)}
                  showMentions={showMentions}
                  filteredMembers={filteredMembers}
                  onInsertMention={insertMention}
                  showPreview={showPreview}
                  onTogglePreview={() => setShowPreview(!showPreview)}
                  textareaRef={textareaRef}
                  compact
                />
              </div>
            </TabsContent>

            {/* Annotations Tab */}
            <TabsContent value="annotations" className="flex-1 min-h-0 m-0 overflow-hidden data-[state=active]:grid data-[state=active]:grid-rows-[auto_minmax(0,1fr)_auto]">
              {/* Header - fixed */}
              <div className="flex-shrink-0 p-3 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-600">{marks.length} annotation{marks.length !== 1 ? 's' : ''}</span>
                <Button
                  size="sm"
                  onClick={() => { setIsAddingMark(!isAddingMark); setIsAddingPrivateNote(false); }}
                  className={isAddingMark ? "bg-purple-600" : ""}
                  variant={isAddingMark ? "default" : "outline"}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Mark
                </Button>
              </div>

              {/* Scrollable annotations list */}
              <div className="min-h-0 overflow-y-auto">
                {marks.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 px-4">
                    <MapPin className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium mb-1">No annotations yet</p>
                    <p className="text-sm">Select text on the document or click "Add Mark" to place an annotation.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {marks.map(mark => (
                      <MarkSection
                        key={mark.id}
                        mark={mark}
                        comments={getCommentsForMark(mark.id)}
                        allComments={comments}
                        isSelected={selectedMark?.id === mark.id}
                        onSelect={() => setSelectedMark(mark)}
                        onAddComment={() => { setSelectedMark(mark); setReplyingTo(null); setTimeout(() => textareaRef.current?.focus(), 0); }}
                        onDelete={() => deleteMark(mark.id)}
                        onResolve={resolveComment}
                        onDeleteComment={deleteComment}
                        currentUserId={currentUserId}
                        onReply={(comment) => {
                          setSelectedMark(mark);
                          setReplyingTo(comment);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Comment Input for Selected Mark - Fixed at bottom */}
              <div className="border-t border-gray-200 bg-gray-50">
                {selectedMark ? (
                  <>
                    <div className="px-3 py-2 bg-purple-50 flex items-start gap-2 border-b border-purple-100">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold bg-gradient-to-br from-purple-500 to-pink-500 flex-shrink-0 mt-0.5">
                        {selectedMark.label}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-purple-900">Add comment to annotation #{selectedMark.label}</div>
                        {selectedMark.highlighted_text && (
                          <div className="text-xs text-purple-700 truncate italic">“{selectedMark.highlighted_text}”</div>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => { setSelectedMark(null); setReplyingTo(null); }} className="h-7 px-2 text-xs text-purple-700 hover:bg-purple-100">
                        Done
                      </Button>
                    </div>
                    <CommentInput
                      value={newComment}
                      onChange={handleCommentChange}
                      onSubmit={() => submitComment(replyingTo?.id, selectedMark.id)}
                      onFormatting={insertFormatting}
                      onInsertLink={insertLink}
                      submitting={submitting}
                      replyingTo={replyingTo}
                      onCancelReply={() => setReplyingTo(null)}
                      showMentions={showMentions}
                      filteredMembers={filteredMembers}
                      onInsertMention={insertMention}
                      showPreview={showPreview}
                      onTogglePreview={() => setShowPreview(!showPreview)}
                      textareaRef={textareaRef}
                      compact
                    />
                  </>
                ) : (
                  <div className="p-3 text-center text-sm text-gray-500 bg-gray-50">
                    Select an annotation above to add a comment
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Private Notes Tab */}
            <TabsContent value="private" className="flex-1 min-h-0 m-0 overflow-hidden data-[state=active]:grid data-[state=active]:grid-rows-[auto_minmax(0,1fr)_auto]">
              <div className="flex-shrink-0 p-3 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-600">{privateNotes.length} private note{privateNotes.length !== 1 ? 's' : ''}</span>
                <Button
                  size="sm"
                  onClick={() => { setIsAddingPrivateNote(!isAddingPrivateNote); setIsAddingMark(false); }}
                  className={isAddingPrivateNote ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white" : ""}
                  variant={isAddingPrivateNote ? "default" : "outline"}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Note
                </Button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                {privateNotes.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 px-4">
                    <StickyNote className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium mb-1">No private notes yet</p>
                    <p className="text-sm">Select text or click “Add Note” to create notes only you can see.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {privateNotes.map((note, index) => (
                      <PrivateNoteSection
                        key={note.id}
                        note={note}
                        index={index}
                        isSelected={selectedPrivateNote?.id === note.id}
                        onSelect={() => setSelectedPrivateNote(note)}
                        onUpdate={updatePrivateNote}
                        onDelete={deletePrivateNote}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="flex-shrink-0 p-3 text-xs text-gray-500 bg-purple-50 border-t border-purple-100">
                Private notes are scoped to your account and are not shared with the board or committee.
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// DOCUMENT PREVIEW COMPONENT
// =============================================================================

// =============================================================================
// PDF VIEWER WITH TEXT SELECTION
// =============================================================================

const PDFViewer = ({
  url,
  onTextSelect,
  marks = [],
  selectedMarkId,
  onSelectMark,
  isAddingMark = false,
  onPlaceMark,
  onCancelAddMark,
  showAnnotations = true,
  privateNotes = [],
  selectedPrivateNote,
  onSelectPrivateNote,
  draftPrivateNote,
  setDraftPrivateNote,
  saveDraftPrivateNote,
  cancelDraftPrivateNote,
  isAddingPrivateNote = false,
  onPlacePrivateNote
}) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.3); // Larger default zoom
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const containerRef = useRef(null);
  const pdfWrapperRef = useRef(null);

  // Fetch PDF with auth token
  useEffect(() => {
    const fetchPdf = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        setPdfData({ data: arrayBuffer });
      } catch (err) {
        console.error('Failed to fetch PDF:', err);
        setError('Failed to load PDF');
        setLoading(false);
      }
    };

    if (url) {
      fetchPdf();
    }
  }, [url]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setError('Failed to load PDF');
    setLoading(false);
  };

  // Handle text selection - capture text and bounding rectangles
  const handleMouseUp = () => {
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim();

    if (selectedText && selectedText.length > 0) {
      // Get selection position for popup placement
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();
      // Use pdfWrapperRef for rectangle calculations (where highlights are rendered)
      const pdfRect = pdfWrapperRef.current?.getBoundingClientRect();

      if (containerRect && pdfRect) {
        // Position for popup (relative to scroll container)
        const x = ((rect.left + rect.width / 2 - containerRect.left) / containerRect.width) * 100;
        const y = ((rect.top - containerRect.top) / containerRect.height) * 100;

        // Capture all selection rectangles for persistent highlighting
        // These must be relative to the PDF wrapper where highlights are rendered
        const clientRects = range.getClientRects();
        const rects = [];

        for (let i = 0; i < clientRects.length; i++) {
          const r = clientRects[i];

          // Skip invalid rectangles (zero or near-zero dimensions)
          if (r.width < 2 || r.height < 2) continue;

          // Calculate position relative to PDF wrapper
          const rectX = ((r.left - pdfRect.left) / pdfRect.width) * 100;
          const rectY = ((r.top - pdfRect.top) / pdfRect.height) * 100;
          const rectWidth = (r.width / pdfRect.width) * 100;
          const rectHeight = (r.height / pdfRect.height) * 100;

          // Only include rectangles that are actually within the PDF bounds
          // and have meaningful dimensions
          if (rectX >= -2 && rectX <= 102 &&
              rectY >= -2 && rectY <= 102 &&
              rectWidth > 0.5 && rectHeight > 0.3) {
            rects.push({
              x: Math.max(0, Math.min(100, rectX)),
              y: Math.max(0, Math.min(100, rectY)),
              width: Math.min(100 - Math.max(0, rectX), rectWidth),
              height: rectHeight,
              pageNumber: pageNumber
            });
          }
        }

        // Deduplicate overlapping rectangles (sometimes getClientRects returns duplicates)
        const deduped = rects.filter((rect, index, arr) => {
          // Keep if no other rectangle is very close to this one
          return !arr.slice(0, index).some(other =>
            Math.abs(rect.x - other.x) < 1 &&
            Math.abs(rect.y - other.y) < 0.5 &&
            Math.abs(rect.width - other.width) < 1
          );
        });

        onTextSelect({
          text: selectedText,
          x: Math.max(0, Math.min(100, x)),
          y: Math.max(0, Math.min(100, y)),
          pageNumber,
          screenX: rect.left + rect.width / 2,
          screenY: rect.top,
          rects: deduped.length > 0 ? deduped : null // Include rectangle coords for visual highlights
        });
      }
    }
  };


  const handlePdfClick = (e) => {
    if ((!isAddingPrivateNote && !isAddingMark) || !pdfWrapperRef.current) return;
    if (draftPrivateNote) return;
    const rect = pdfWrapperRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    if (isAddingMark) {
      onPlaceMark?.({ x_percent: x, y_percent: y, page_number: pageNumber });
      return;
    }
    onPlacePrivateNote?.({
      x_percent: x,
      y_percent: y,
      page_number: pageNumber
    });
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-gray-50">
        <FileText className="h-16 w-16 text-gray-300 mb-4" />
        <p className="text-gray-500">{error}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => window.open(url, '_blank')}
        >
          <Download className="h-4 w-4 mr-2" />
          Download PDF
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* PDF Controls - simplified for continuous scroll */}
      <div className="flex items-center justify-end px-4 py-2 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setScale(s => Math.max(0.5, s - 0.25))}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm text-gray-600 min-w-[50px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setScale(s => Math.min(2, s + 0.25))}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <span className="text-sm text-gray-500 ml-2">
            {numPages || '...'} page{numPages !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* PDF Content - continuous scroll */}
      <div
        ref={containerRef}
        className={`flex-1 overflow-auto p-4 ${(isAddingPrivateNote || isAddingMark) ? 'cursor-crosshair' : ''}`}
        onMouseUp={handleMouseUp}
        onClick={handlePdfClick}
      >
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          </div>
        )}

        <div className="flex flex-col items-center gap-4">
          {pdfData && (
            <div ref={pdfWrapperRef} className="relative">
              <Document
                file={pdfData}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={null}
                className="shadow-lg"
              >
                {/* Render all pages in continuous scroll */}
                {Array.from({ length: numPages || 0 }, (_, i) => i + 1).map((pageNum) => (
                  <Page
                    key={pageNum}
                    pageNumber={pageNum}
                    scale={scale}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    className="bg-white mb-4 shadow-lg"
                  />
                ))}
              </Document>
              {/* Mark highlights and pins rendered directly on the PDF content so they scroll with the page */}
              {showAnnotations && marks.map(mark => (
                <div key={mark.id}>
                  {mark.highlighted_text && (
                    <div
                      className={`absolute z-5 pointer-events-none transition-opacity ${
                        selectedMarkId === mark.id ? 'opacity-100' : 'opacity-60'
                      }`}
                      style={{
                        left: `${Math.max(0, mark.x_percent - 2)}%`,
                        top: `${mark.y_percent}%`,
                        transform: 'translateY(-50%)'
                      }}
                    >
                      <div className="bg-purple-200/60 px-1 py-0.5 rounded text-xs text-purple-900 font-medium max-w-[200px] truncate border border-purple-400/50">
                        {mark.highlighted_text.substring(0, 40)}{mark.highlighted_text.length > 40 ? '...' : ''}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectMark?.(mark);
                    }}
                    className={`absolute z-30 transform -translate-x-1/2 -translate-y-1/2 transition-all ${
                      selectedMarkId === mark.id ? 'scale-110 ring-2 ring-white ring-offset-1' : 'hover:scale-105'
                    }`}
                    style={{ left: `${mark.x_percent}%`, top: `${mark.y_percent}%` }}
                    title={`#${mark.label}: ${mark.comment_count || 0} comment(s)`}
                  >
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold shadow-md bg-gradient-to-br from-purple-500 to-pink-500 border-2 border-white">
                      {mark.label}
                    </div>
                  </button>
                </div>
              ))}

              {isAddingMark && (
                <div className="absolute inset-0 z-20 pointer-events-none">
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg flex items-center gap-2 bg-purple-600 pointer-events-auto">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    Click the document to place a mark
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onCancelAddMark?.(); }} className="text-white h-6 w-6 p-0 ml-2 hover:bg-purple-700">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Private notes rendered directly on the PDF content so they scroll with the page */}
            {showAnnotations && privateNotes.map((note, index) => (
              <div key={note.id}>
                {note.highlighted_text && (
                  <div
                    className={`absolute z-5 pointer-events-none transition-opacity ${
                      selectedPrivateNote?.id === note.id ? 'opacity-100' : 'opacity-70'
                    }`}
                    style={{
                      left: `${Math.max(0, (note.x_percent || 0) - 2)}%`,
                      top: `${note.y_percent || 0}%`,
                      transform: 'translateY(-50%)'
                    }}
                  >
                    <div className="bg-purple-200/70 px-1 py-0.5 rounded text-xs text-purple-900 font-medium max-w-[200px] truncate border border-purple-400/60 border-dashed">
                      {note.highlighted_text.substring(0, 40)}{note.highlighted_text.length > 40 ? '...' : ''}
                    </div>
                  </div>
                )}
                {note.x_percent !== null && note.y_percent !== null && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectPrivateNote?.(note);
                    }}
                    className={`absolute z-20 w-56 text-left rounded-xl border shadow-lg transition-all ${
                      selectedPrivateNote?.id === note.id
                        ? 'scale-[1.02] border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                        : 'border-amber-300 bg-amber-50/95 hover:bg-purple-100 hover:scale-[1.01]'
                    }`}
                    style={{
                      left: `${note.x_percent}%`,
                      top: `${note.y_percent}%`,
                      transform: 'translate(-12px, -12px)'
                    }}
                    title={`Private note ${index + 1}`}
                  >
                    <div className="flex items-start gap-2 p-3">
                      <div className="mt-0.5 h-6 w-6 flex-shrink-0 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white flex items-center justify-center shadow-sm">
                        <StickyNote className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-purple-700 mb-1">Private note</div>
                        <div className="text-sm text-gray-900 whitespace-pre-wrap line-clamp-4">{note.content}</div>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            ))}

            {/* Inline private-note composer. This replaces browser prompt() with an app-native document note. */}
            {draftPrivateNote && (
              <div
                className="absolute z-40 w-72 rounded-xl border border-purple-300 bg-white shadow-2xl"
                style={{
                  left: `${draftPrivateNote.x_percent}%`,
                  top: `${draftPrivateNote.y_percent}%`,
                  transform: 'translate(-12px, -12px)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between border-b border-purple-100 bg-purple-50 px-3 py-2 rounded-t-xl">
                  <div className="flex items-center gap-2 text-sm font-semibold text-purple-800">
                    <StickyNote className="h-4 w-4" />
                    Private note
                  </div>
                  <Button variant="ghost" size="sm" onClick={cancelDraftPrivateNote} className="h-7 w-7 p-0 text-purple-700 hover:bg-purple-100">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {draftPrivateNote.highlighted_text && (
                  <div className="mx-3 mt-3 rounded-lg bg-purple-50 px-3 py-2 text-xs text-purple-900 border border-purple-100 line-clamp-3">
                    “{draftPrivateNote.highlighted_text}”
                  </div>
                )}
                <div className="p-3 space-y-3">
                  <Textarea
                    autoFocus
                    value={draftPrivateNote.content}
                    onChange={(e) => setDraftPrivateNote(draft => ({ ...draft, content: e.target.value }))}
                    placeholder="Type your private note..."
                    className="min-h-[110px] resize-none border-purple-200 focus-visible:ring-purple-500"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        saveDraftPrivateNote();
                      }
                      if (e.key === 'Escape') cancelDraftPrivateNote();
                    }}
                  />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] text-gray-500">Only you can see this.</span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={cancelDraftPrivateNote}>Cancel</Button>
                      <Button size="sm" onClick={saveDraftPrivateNote} disabled={!draftPrivateNote.content.trim()} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                        Add Note
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// DOCUMENT PREVIEW COMPONENT
// =============================================================================

const DocumentPreview = ({
  document: doc,
  onTextSelect,
  marks = [],
  selectedMarkId,
  onSelectMark,
  isAddingMark = false,
  onPlaceMark,
  onCancelAddMark,
  showAnnotations = true,
  committeeId,
  privateNotes = [],
  selectedPrivateNote,
  onSelectPrivateNote,
  draftPrivateNote,
  setDraftPrivateNote,
  saveDraftPrivateNote,
  cancelDraftPrivateNote,
  isAddingPrivateNote = false,
  onPlacePrivateNote
}) => {
  if (!doc) return null;

  // Get the proxy URL for PDF files (bypasses CORS)
  const getPdfProxyUrl = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const url = `${baseUrl}/api/board/documents/${doc.id}/pdf-proxy`;
    return committeeId ? `${url}?committee_id=${committeeId}` : url;
  };

  // Helper to create Google Docs Viewer URL for PDFs (handles cross-origin)
  const getGoogleViewerUrl = (url) => {
    return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
  };

  // Images
  if (doc.mime_type?.includes('image')) {
    return (
      <div className="flex items-center justify-center h-full p-4 bg-gray-50">
        <img
          src={doc.content_url || doc.file_url}
          alt={doc.name}
          className="max-w-full max-h-full object-contain rounded-lg shadow-sm"
          draggable={false}
        />
      </div>
    );
  }

  // PDFs - use react-pdf via proxy for native rendering with text selection
  if (doc.mime_type?.includes('pdf') || doc.pdf_preview_url) {
    return <PDFViewer
      url={getPdfProxyUrl()}
      onTextSelect={onTextSelect}
      marks={marks}
      selectedMarkId={selectedMarkId}
      onSelectMark={onSelectMark}
      isAddingMark={isAddingMark}
      onPlaceMark={onPlaceMark}
      onCancelAddMark={onCancelAddMark}
      showAnnotations={showAnnotations}
      privateNotes={privateNotes}
      selectedPrivateNote={selectedPrivateNote}
      onSelectPrivateNote={onSelectPrivateNote}
      draftPrivateNote={draftPrivateNote}
      setDraftPrivateNote={setDraftPrivateNote}
      saveDraftPrivateNote={saveDraftPrivateNote}
      cancelDraftPrivateNote={cancelDraftPrivateNote}
      isAddingPrivateNote={isAddingPrivateNote}
      onPlacePrivateNote={onPlacePrivateNote}
    />;
  }

  // Videos
  if (doc.mime_type?.includes('video')) {
    return (
      <div className="flex items-center justify-center h-full p-4 bg-gray-50">
        <video
          src={doc.content_url || doc.file_url}
          controls
          className="max-w-full max-h-full rounded-lg shadow-sm"
        >
          Your browser does not support video playback.
        </video>
      </div>
    );
  }

  // Office documents - need PDF conversion for text selection support
  if (doc.mime_type?.includes('word') ||
      doc.mime_type?.includes('document') ||
      doc.mime_type?.includes('spreadsheet') ||
      doc.mime_type?.includes('excel') ||
      doc.mime_type?.includes('presentation') ||
      doc.mime_type?.includes('powerpoint') ||
      doc.original_filename?.match(/\.(docx?|xlsx?|pptx?)$/i)) {
    // Show message that conversion is needed
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-gray-50">
        <div className="p-6 bg-purple-100 rounded-xl mb-6">
          <FileText className="h-16 w-16 text-purple-600" />
        </div>
        <p className="text-lg font-medium text-gray-700 mb-2">{doc.name}</p>
        <p className="text-sm text-gray-500 mb-4">This document needs to be converted for preview.</p>
        <p className="text-xs text-gray-400 mb-6">Re-upload the document to enable preview and annotations.</p>
        {doc.can_download && doc.file_url ? <Button onClick={() => window.open(doc.file_url, '_blank')} size="lg">
          <Download className="h-5 w-5 mr-2" />
          Download Original
        </Button> : <p className="text-sm text-gray-500">Preview only. Download disabled for this document.</p>}
      </div>
    );
  }

  // Text files
  if (doc.mime_type?.includes('text') || doc.original_filename?.match(/\.(txt|csv|json|xml|md)$/i)) {
    if (doc.pdf_preview_url) {
      return (
        <iframe
          src={doc.pdf_preview_url}
          className="w-full h-full border-0"
          title={doc.name}
        />
      );
    }
    return (
      <iframe
        src={doc.content_url || doc.file_url}
        className="w-full h-full border-0 bg-white"
        title={doc.name}
      />
    );
  }

  // Default fallback
  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-gray-50">
      <div className="p-6 bg-gray-100 rounded-xl mb-6">
        <File className="h-16 w-16 text-gray-400" />
      </div>
      <p className="text-lg font-medium text-gray-700 mb-2">{doc.name}</p>
      <p className="text-gray-500 mb-6">Preview not available for this file type</p>
      {doc.can_download && doc.file_url ? <Button onClick={() => window.open(doc.file_url, '_blank')} size="lg">
        <Download className="h-5 w-5 mr-2" />
        Download
      </Button> : <p className="text-sm text-gray-500">Preview only. Download disabled for this document.</p>}
    </div>
  );
};

// =============================================================================
// COMMENT INPUT COMPONENT
// =============================================================================

const CommentInput = ({
  value,
  onChange,
  onSubmit,
  onFormatting,
  onInsertLink,
  submitting,
  replyingTo,
  onCancelReply,
  showMentions,
  filteredMembers,
  onInsertMention,
  showPreview,
  onTogglePreview,
  textareaRef,
  compact = false
}) => {
  return (
    <div className="p-3 bg-gray-50 shadow-[0_-1px_3px_rgba(0,0,0,0.04)]">
      {replyingTo && (
        <div className="flex items-center gap-2 p-2 mb-2 bg-purple-50 rounded-lg border border-purple-200">
          <Reply className="h-3 w-3 text-purple-600" />
          <span className="text-xs text-purple-700">Replying to {replyingTo.author?.name || 'Unknown'}</span>
          <Button variant="ghost" size="sm" onClick={onCancelReply} className="ml-auto h-5 w-5 p-0">
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Compact Markdown Toolbar */}
      <div className="flex items-center gap-1.5 pb-2 mb-2 border-b border-gray-200">
        <Button variant="ghost" size="sm" onClick={() => onFormatting('**', '**')} title="Bold" className="h-6 w-6 p-0">
          <Bold className="h-3 w-3" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onFormatting('*', '*')} title="Italic" className="h-6 w-6 p-0">
          <Italic className="h-3 w-3" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onInsertLink} title="Insert Link" className="h-6 w-6 p-0">
          <LinkIcon className="h-3 w-3" />
        </Button>
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="sm"
          onClick={onTogglePreview}
          className={`text-xs h-7 px-2 ${showPreview ? 'bg-purple-100 text-purple-700' : ''}`}
        >
          Preview
        </Button>
      </div>

      <div className="relative">
        {showPreview ? (
          <div className="min-h-[50px] p-2 bg-white rounded-lg border border-gray-200 prose prose-sm max-w-none text-sm">
            <ReactMarkdown
              components={{
                a: ({node, ...props}) => <a {...props} className="text-purple-600 hover:underline" target="_blank" rel="noopener noreferrer" />,
                p: ({node, ...props}) => <p {...props} className="mb-1 last:mb-0" />,
              }}
            >
              {value || '*Nothing to preview*'}
            </ReactMarkdown>
          </div>
        ) : (
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={onChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSubmit();
              }
            }}
            placeholder="Add a comment..."
            rows={2}
            className="resize-none bg-white text-sm min-h-[72px] w-full"
          />
        )}

        {/* @Mentions Dropdown */}
        {showMentions && filteredMembers.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-32 overflow-auto z-50">
            <div className="p-1.5 text-[10px] font-medium text-gray-500 border-b">Board Members</div>
            {filteredMembers.map(member => (
              <button
                key={member.id}
                onClick={() => onInsertMention(member)}
                className="w-full px-2 py-1.5 text-left hover:bg-gray-50 flex items-center gap-2"
              >
                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-[10px] font-medium">
                  {member.name?.charAt(0) || '?'}
                </div>
                <span className="text-xs">{member.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end mt-3">
        <Button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSubmit(); }} disabled={!value.trim() || submitting} size="sm" className="h-8 text-xs px-3">
          {submitting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
          {replyingTo ? 'Reply' : 'Comment'}
        </Button>
      </div>
    </div>
  );
};

// =============================================================================
// COMMENT THREAD COMPONENT
// =============================================================================

const CommentThread = ({ comment, replies, onResolve, onReply, onDelete, currentUserId }) => {
  const authorName = comment.author?.name || 'Unknown';

  return (
    <div>
      <CommentCard
        comment={comment}
        authorName={authorName}
        onResolve={() => onResolve(comment.id)}
        onReply={() => onReply(comment)}
        onDelete={() => onDelete(comment.id)}
        currentUserId={currentUserId}
      />

      {replies && replies.length > 0 && (
        <div className="ml-8 mt-2 space-y-2 border-l-2 border-purple-100 pl-4">
          {replies.map(reply => (
            <CommentCard
              key={reply.id}
              comment={reply}
              authorName={reply.author?.name || 'Unknown'}
              onResolve={() => onResolve(reply.id)}
              onReply={() => onReply(reply)}
              onDelete={() => onDelete(reply.id)}
              currentUserId={currentUserId}
              isReply
            />
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// COMMENT CARD COMPONENT
// =============================================================================

const CommentCard = ({ comment, authorName, onResolve, onReply, onDelete, currentUserId, isReply = false }) => {
  const avatarUrl = comment?.author?.avatar_url;
  const canDelete = !!currentUserId && String(comment?.author?.id || '') === String(currentUserId);
  return (
    <div className={`p-4 rounded-lg border ${comment.is_resolved ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'} ${isReply ? 'py-3' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className={`${isReply ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-sm'} rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-medium overflow-hidden`}>
            {avatarUrl ? (
              <img src={avatarUrl} alt={authorName} className="w-full h-full object-cover" />
            ) : (
              authorName.charAt(0)
            )}
          </div>
          <div>
            <p className={`font-medium text-gray-900 ${isReply ? 'text-xs' : 'text-sm'}`}>{authorName}</p>
            <p className="text-xs text-gray-400">{formatDate(comment.created_at)}</p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {!comment.is_resolved && onReply && (
            <Button variant="ghost" size="sm" onClick={onReply} className="text-purple-600 hover:text-purple-700 h-7 px-2">
              <Reply className="h-3 w-3 mr-1" />
              <span className="text-xs">Reply</span>
            </Button>
          )}

          {!comment.is_resolved && (
            <Button variant="ghost" size="sm" onClick={onResolve} className="text-green-600 hover:text-green-700 h-7 w-7 p-0" title="Mark as resolved">
              <CheckCircle className="h-4 w-4" />
            </Button>
          )}

          {canDelete && (
            <Button variant="ghost" size="sm" onClick={onDelete} className="text-gray-500 hover:text-red-600 h-7 w-7 p-0" title="Delete comment">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}

          {comment.is_resolved && (
            <Badge className="bg-green-100 text-green-700 border-0 text-xs">
              <CheckCircle className="h-3 w-3 mr-1" />
              Resolved
            </Badge>
          )}
        </div>
      </div>

      <div className={`mt-3 text-gray-700 ${isReply ? 'text-xs' : 'text-sm'} prose prose-sm max-w-none`}>
        <ReactMarkdown
          components={{
            a: ({node, ...props}) => (
              <a {...props} className="text-purple-600 hover:underline" target="_blank" rel="noopener noreferrer" />
            ),
            p: ({node, ...props}) => <p {...props} className="mb-2 last:mb-0" />,
            strong: ({node, ...props}) => <strong {...props} className="font-semibold" />,
            ul: ({node, ...props}) => <ul {...props} className="list-disc list-inside mb-2" />,
            li: ({node, ...props}) => <li {...props} className="mb-1" />
          }}
        >
          {comment.content}
        </ReactMarkdown>
      </div>
    </div>
  );
};

// =============================================================================
// MARK SECTION COMPONENT
// =============================================================================

const PrivateNoteSection = ({ note, index, isSelected, onSelect, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(note.content || '');

  useEffect(() => {
    setDraft(note.content || '');
  }, [note.content]);

  const save = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onUpdate(note.id, trimmed);
    setIsEditing(false);
  };

  return (
    <div className={`${isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'} transition-colors`}>
      <div className="p-3 flex items-start gap-3 cursor-pointer" onClick={onSelect}>
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white shrink-0 bg-gradient-to-br from-purple-500 to-pink-500 border-2 border-white shadow-sm">
          <StickyNote className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900 text-sm">Private note {index + 1}</span>
            {note.highlighted_text ? (
              <Badge className="bg-purple-100 text-purple-700 border-0 text-xs">Highlight</Badge>
            ) : note.x_percent !== null && note.y_percent !== null ? (
              <Badge className="bg-pink-100 text-pink-700 border-0 text-xs">Pin</Badge>
            ) : null}
          </div>
          {note.highlighted_text && (
            <p className="text-xs text-purple-700 truncate italic mb-2">“{note.highlighted_text}”</p>
          )}
          {isEditing ? (
            <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    save();
                  }
                }}
                rows={3}
                className="bg-white text-sm"
              />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setDraft(note.content || ''); setIsEditing(false); }}>Cancel</Button>
                <Button size="sm" onClick={save} disabled={!draft.trim()} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">Save</Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.content}</p>
          )}
          <p className="text-xs text-gray-400 mt-2">{formatDate(note.updated_at || note.created_at)}</p>
        </div>
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {!isEditing && (
            <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="h-7 px-2 text-purple-700 hover:text-purple-800">
              Edit
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => onDelete(note.id)} className="h-7 w-7 p-0 text-gray-400 hover:text-red-600">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const MarkSection = ({ mark, comments, allComments, isSelected, onSelect, onAddComment, onDelete, onResolve, onReply, onDeleteComment, currentUserId }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const getReplies = (commentId) => allComments.filter(c => c.parent_comment_id === commentId);

  const latestComment = comments[comments.length - 1];

  return (
    <div className={`${isSelected ? 'bg-purple-50 ring-1 ring-purple-200' : 'hover:bg-gray-50'} transition-colors`}>
      <div className="p-3 space-y-2">
        {/* Mark Header */}
        <div className="flex items-start gap-3">
          <button
            type="button"
            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 bg-gradient-to-br from-purple-500 to-pink-500 mt-0.5"
            onClick={onSelect}
            title={`Select annotation #${mark.label}`}
          >
            {mark.label}
          </button>
          <button type="button" className="flex-1 min-w-0 text-left" onClick={onSelect}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-sm">Annotation #{mark.label}</span>
              <Badge variant="secondary" className="text-xs">
                {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
              </Badge>
              <span className="text-xs text-gray-400">Page {mark.page_number}</span>
            </div>
            {mark.highlighted_text ? (
              <p className="text-xs text-gray-600 line-clamp-2 italic mt-0.5">“{mark.highlighted_text}”</p>
            ) : latestComment ? (
              <p className="text-xs text-gray-600 line-clamp-2 mt-0.5">{latestComment.content}</p>
            ) : (
              <p className="text-xs text-gray-500 mt-0.5">No text preview captured</p>
            )}
          </button>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
              className="h-7 px-2 text-xs text-gray-600 hover:text-purple-700"
              title={isExpanded ? 'Collapse comments' : 'Show comments'}
            >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onAddComment(); }}
              className="h-7 px-2 text-xs text-purple-700 hover:bg-purple-100"
            >
              Comment
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="h-7 w-7 p-0 text-gray-400 hover:text-red-600"
              title="Delete annotation and its comments"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Comments */}
        {isExpanded && (
          <div className="pl-9 space-y-2">
            {comments.length > 0 ? comments.map(comment => (
              <div key={comment.id}>
                <CommentCard
                  comment={comment}
                  authorName={comment.author?.name || 'Unknown'}
                  onResolve={() => onResolve(comment.id)}
                  onReply={() => onReply(comment)}
                  onDelete={() => onDeleteComment(comment.id)}
                  currentUserId={currentUserId}
                />
                {getReplies(comment.id).length > 0 && (
                  <div className="ml-4 mt-2 pl-3 border-l-2 border-purple-100 space-y-2">
                    {getReplies(comment.id).map(reply => (
                      <CommentCard
                        key={reply.id}
                        comment={reply}
                        authorName={reply.author?.name || 'Unknown'}
                        onResolve={() => onResolve(reply.id)}
                        onReply={() => onReply(reply)}
                        onDelete={() => onDeleteComment(reply.id)}
                        currentUserId={currentUserId}
                        isReply
                      />
                    ))}
                  </div>
                )}
              </div>
            )) : (
              <button
                type="button"
                onClick={onAddComment}
                className="w-full rounded-lg border border-dashed border-purple-200 bg-white px-3 py-2 text-left text-xs text-purple-700 hover:bg-purple-50"
              >
                Add the first comment to this annotation
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;
